import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

/**
 * A language switch must re-ask the server: text the API renders in the request's `Accept-Language`
 * (homework default titles since 1.7.0, coded error copy) stays in the old language until the next fetch otherwise.
 * Two steps, in this order: the OUTGOING header is the new language first (synchronously, not in a post-render
 * effect), then the active queries are invalidated so they refetch with it.
 */
type HeaderSink = { setHeader: (key: string, value: string) => unknown };
type LanguageSwitcher = { changeLanguage: (lng: string) => Promise<unknown> | unknown };

export const applyLocale = async (lang: string, i18n: LanguageSwitcher, client: HeaderSink): Promise<void> => {
  client.setHeader('Accept-Language', lang);
  await i18n.changeLanguage(lang);
};

/** Inside Refine's QueryClient: when the language changes (not on mount), refetch every active query. */
export const useRefetchOnLocaleChange = (language: string | undefined): void => {
  const queryClient = useQueryClient();
  const previous = useRef(language);
  useEffect(() => {
    if (previous.current === language) return;
    previous.current = language;
    void queryClient.invalidateQueries();
  }, [language, queryClient]);
};
